import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Truck, History, Calculator, Plus } from "lucide-react";

/**
 * Nakliye satırı seçici modal.
 * Fiyat Hesapla ekranından çağrılır.
 *
 * Props:
 *   onClose(): void
 *   onAdd(line): void  // line = { source, total_cost, distance_km, return_trip, vehicle_name, from_location, to_location, note }
 */
export default function ShippingPickerModal({ onClose, onAdd }) {
  const [tab, setTab] = useState("quick"); // "history" | "quick"
  const [history, setHistory] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [company, setCompany] = useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: hi }, { data: tr }, { data: cp }] = await Promise.all([
          api.get("/shipping-calcs"),
          api.get("/vehicle-tariffs"),
          api.get("/settings/company"),
        ]);
        setHistory(hi || []);
        setTariffs(tr || []);
        setCompany(cp || null);
      } catch (e) {
        toast.error("Nakliye verileri yüklenemedi");
      }
    })();
  }, []);

  const defaultTariff = useMemo(
    () => tariffs.find((t) => t.is_default) || tariffs[0] || null,
    [tariffs]
  );

  const addFromHistory = () => {
    const h = history.find((x) => x.id === selectedHistoryId);
    if (!h) { toast.error("Geçmişten bir kayıt seçin"); return; }
    onAdd({
      source: "history",
      history_id: h.id,
      total_cost: h.total_cost,
      distance_km: h.distance_km,
      return_trip: !!h.return_trip,
      vehicle_name: h.vehicle_name || "—",
      from_location: h.from_location,
      to_location: h.to_location,
      note: h.notes || "",
    });
    toast.success("Nakliye eklendi");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ship-picker-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="ship-picker-modal"
        className="bg-white border-2 border-black w-full max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Truck size={16} /> Nakliye Satırı Ekle
          </h2>
          <button
            onClick={onClose}
            data-testid="ship-picker-close"
            className="p-1 text-xl leading-none"
          >×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-black/10 bg-neutral-50">
          <TabBtn
            active={tab === "history"}
            onClick={() => setTab("history")}
            testid="tab-history"
            icon={<History size={13} />}
            label={`Geçmişten Seç (${history.length})`}
          />
          <TabBtn
            active={tab === "quick"}
            onClick={() => setTab("quick")}
            testid="tab-quick"
            icon={<Calculator size={13} />}
            label="Hızlı Hesapla"
          />
        </div>

        {tab === "history" ? (
          <div className="p-5 space-y-4" data-testid="picker-history-panel">
            {history.length === 0 ? (
              <div className="text-sm text-neutral-500 border border-dashed border-black/20 p-6 text-center">
                Kayıtlı nakliye hesabı yok. "Hızlı Hesapla" sekmesinden yeni bir hesap yapabilirsiniz.
              </div>
            ) : (
              <>
                <div className="label-caps">Nakliye Geçmişi</div>
                <select
                  data-testid="picker-history-select"
                  value={selectedHistoryId}
                  onChange={(e) => setSelectedHistoryId(e.target.value)}
                  className="input bg-white"
                >
                  <option value="">— Kayıt seçin —</option>
                  {history.map((h) => (
                    <option key={h.id} value={h.id}>
                      {fmtDate(h.date)} · {h.from_location} → {h.to_location} · {h.distance_km}km{h.return_trip ? "×2" : ""} · {h.vehicle_name || "—"} · {fmtTRY(h.total_cost)}
                    </option>
                  ))}
                </select>
                {selectedHistoryId && (() => {
                  const h = history.find((x) => x.id === selectedHistoryId);
                  if (!h) return null;
                  return (
                    <div className="border-2 border-black p-4 bg-neutral-50 grid grid-cols-2 gap-3 text-sm">
                      <Info label="Tarih" value={fmtDate(h.date)} />
                      <Info label="Araç" value={h.vehicle_name || "—"} />
                      <Info label="Güzergâh" value={`${h.from_location} → ${h.to_location}`} full />
                      <Info label="Mesafe" value={`${h.distance_km} km${h.return_trip ? " ×2" : ""}`} />
                      <Info label="Toplam" value={fmtTRY(h.total_cost)} strong />
                    </div>
                  );
                })()}
                <div className="flex justify-end">
                  <button
                    onClick={addFromHistory}
                    disabled={!selectedHistoryId}
                    data-testid="picker-history-add"
                    className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
                  >
                    <Plus size={14} /> Fiyata Ekle
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <QuickCalcPanel
            tariffs={tariffs}
            defaultTariff={defaultTariff}
            factoryAddr={company?.address || ""}
            onAdd={(line) => { onAdd(line); onClose(); }}
          />
        )}

        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.7rem;outline:none;font-size:0.85rem;background:white}
                    .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, icon, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 px-4 py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
        active ? "border-black bg-white font-bold" : "border-transparent text-neutral-500 hover:bg-neutral-100"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function Info({ label, value, strong, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`mt-0.5 ${strong ? "font-mono-num text-base font-bold text-emerald-700" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function QuickCalcPanel({ tariffs, defaultTariff, factoryAddr, onAdd }) {
  const [form, setForm] = useState({
    date: todayISO(),
    from_location: factoryAddr || "Kartal / İstanbul",
    to_location: "",
    distance_km: "",
    vehicle_tariff_id: defaultTariff?.id || "",
    return_trip: false,
    include_fuel: false,
    fuel_price: "",
    loading_fee: "",
    labor_fee: "",
    notes: "",
    save_to_history: false,
  });

  useEffect(() => {
    if (factoryAddr && !form.from_location) setForm((f) => ({ ...f, from_location: factoryAddr }));
    if (defaultTariff && !form.vehicle_tariff_id) setForm((f) => ({ ...f, vehicle_tariff_id: defaultTariff.id }));
    // eslint-disable-next-line
  }, [factoryAddr, defaultTariff]);

  const tariff = tariffs.find((t) => t.id === form.vehicle_tariff_id) || null;

  const preview = useMemo(() => {
    const distance = parseFloat(form.distance_km) || 0;
    const km_rate = tariff?.km_rate || 0;
    const eff = distance * (form.return_trip ? 2 : 1);
    const km_cost = eff * km_rate;
    let fuel_cost = 0;
    if (form.include_fuel && tariff?.fuel_lt_per_100km) {
      fuel_cost = (eff * tariff.fuel_lt_per_100km) / 100 * (parseFloat(form.fuel_price) || 0);
    }
    const loading = parseFloat(form.loading_fee) || 0;
    const labor = parseFloat(form.labor_fee) || 0;
    const total = km_cost + fuel_cost + loading + labor;
    return { eff, km_cost, fuel_cost, loading, labor, total };
  }, [form, tariff]);

  const submit = async () => {
    if (!form.from_location || !form.to_location) { toast.error("Yükleme ve teslim noktaları zorunlu"); return; }
    const distance = parseFloat(form.distance_km);
    if (isNaN(distance) || distance <= 0) { toast.error("Mesafe girin"); return; }
    if (!form.vehicle_tariff_id) { toast.error("Araç seçin (önce tarife tanımlayın)"); return; }
    if (preview.total <= 0) { toast.error("Toplam 0 olamaz"); return; }

    let history_id = null;
    if (form.save_to_history) {
      try {
        const payload = {
          date: form.date,
          contact_id: null,
          from_location: form.from_location,
          to_location: form.to_location,
          distance_km: distance,
          vehicle_tariff_id: form.vehicle_tariff_id,
          return_trip: form.return_trip,
          include_fuel: form.include_fuel,
          fuel_price: form.include_fuel ? (parseFloat(form.fuel_price) || 0) : null,
          loading_fee: parseFloat(form.loading_fee) || 0,
          labor_fee: parseFloat(form.labor_fee) || 0,
          box_count: null,
          notes: form.notes || "Fiyat Hesapla üzerinden eklendi",
        };
        const { data } = await api.post("/shipping-calcs", payload);
        history_id = data?.id || null;
        toast.success("Nakliye geçmişe kaydedildi");
      } catch (e) {
        toast.error("Geçmişe kaydedilemedi: " + (e?.response?.data?.detail || e?.message || ""));
        return;
      }
    }

    onAdd({
      source: form.save_to_history ? "quick+saved" : "quick",
      history_id,
      total_cost: preview.total,
      distance_km: distance,
      return_trip: form.return_trip,
      vehicle_name: tariff?.name || "—",
      from_location: form.from_location,
      to_location: form.to_location,
      note: form.notes || "",
    });
    toast.success("Nakliye eklendi");
  };

  return (
    <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="picker-quick-panel">
      <div className="lg:col-span-2 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Yükleme Noktası">
            <input data-testid="picker-from" value={form.from_location}
                   onChange={(e) => setForm({ ...form, from_location: e.target.value })}
                   className="input" placeholder="Kartal / İstanbul" />
          </FLabel>
          <FLabel label="Teslim Noktası">
            <input data-testid="picker-to" value={form.to_location}
                   onChange={(e) => setForm({ ...form, to_location: e.target.value })}
                   className="input" placeholder="Bursa / Nilüfer" />
          </FLabel>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Mesafe (km)">
            <input type="number" step="0.1" data-testid="picker-distance" value={form.distance_km}
                   onChange={(e) => setForm({ ...form, distance_km: e.target.value })}
                   className="input font-mono-num" />
          </FLabel>
          <FLabel label="Araç">
            <select data-testid="picker-vehicle" value={form.vehicle_tariff_id}
                    onChange={(e) => setForm({ ...form, vehicle_tariff_id: e.target.value })} className="input">
              {tariffs.length === 0 && <option value="">— Önce tarife tanımlayın —</option>}
              {tariffs.map((t) => <option key={t.id} value={t.id}>{t.name} · {fmtTRY(t.km_rate)}/km</option>)}
            </select>
          </FLabel>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Sabit Yükleme Ücreti">
            <input type="number" step="0.01" data-testid="picker-loading" value={form.loading_fee}
                   onChange={(e) => setForm({ ...form, loading_fee: e.target.value })}
                   className="input font-mono-num" placeholder="₺" />
          </FLabel>
          <FLabel label="Sürücü/İşçilik">
            <input type="number" step="0.01" data-testid="picker-labor" value={form.labor_fee}
                   onChange={(e) => setForm({ ...form, labor_fee: e.target.value })}
                   className="input font-mono-num" placeholder="₺" />
          </FLabel>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="picker-return" checked={form.return_trip}
                   onChange={(e) => setForm({ ...form, return_trip: e.target.checked })}
                   className="w-4 h-4 accent-black" />
            İade dönüşü var (×2 mesafe)
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="picker-fuel-toggle" checked={form.include_fuel}
                   onChange={(e) => setForm({ ...form, include_fuel: e.target.checked })}
                   className="w-4 h-4 accent-black" />
            Yakıt ayrıca hesaplansın
          </label>
          {form.include_fuel && (
            <label className="inline-flex items-center gap-2">
              <span className="text-xs text-neutral-500">Yakıt (₺/L):</span>
              <input type="number" step="0.01" data-testid="picker-fuel-price" value={form.fuel_price}
                     onChange={(e) => setForm({ ...form, fuel_price: e.target.value })}
                     className="border border-black/20 px-2 py-1 w-24 text-xs font-mono-num" />
            </label>
          )}
        </div>
        <FLabel label="Not (opsiyonel)">
          <input data-testid="picker-notes" value={form.notes}
                 onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
        </FLabel>
        <label className="inline-flex items-center gap-2 cursor-pointer pt-1">
          <input type="checkbox" data-testid="picker-save-history" checked={form.save_to_history}
                 onChange={(e) => setForm({ ...form, save_to_history: e.target.checked })}
                 className="w-4 h-4 accent-black" />
          <span className="text-sm">Nakliye Geçmişi'ne de kaydet</span>
        </label>
      </div>

      <aside className="border-2 border-black p-4 bg-neutral-50 space-y-2">
        <div className="flex items-center gap-2 label-caps"><Calculator size={12} /> Ön İzleme</div>
        <PreviewRow label="Efektif mesafe" value={`${preview.eff.toLocaleString("tr-TR", { minimumFractionDigits: 1 })} km`} />
        <PreviewRow label="Km × Araç" value={fmtTRY(preview.km_cost)} />
        {form.include_fuel && <PreviewRow label="Yakıt" value={fmtTRY(preview.fuel_cost)} />}
        <PreviewRow label="Yükleme" value={fmtTRY(preview.loading)} />
        <PreviewRow label="İşçilik" value={fmtTRY(preview.labor)} />
        <div className="border-t-2 border-black pt-2 mt-2">
          <PreviewRow label="TOPLAM" value={fmtTRY(preview.total)} big />
        </div>
        <button onClick={submit} disabled={preview.total <= 0} data-testid="picker-quick-add"
                className="brutal-btn bg-black text-white w-full py-2.5 text-sm mt-3 flex items-center justify-center gap-2 disabled:opacity-40">
          <Plus size={14} /> Fiyata Ekle
        </button>
      </aside>
    </div>
  );
}

function FLabel({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function PreviewRow({ label, value, big }) {
  return (
    <div className="flex items-center justify-between">
      <span className={big ? "font-display font-bold" : "text-xs text-neutral-600"}>{label}</span>
      <span className={`font-mono-num ${big ? "text-lg font-bold" : ""}`}>{value}</span>
    </div>
  );
}
